import { AlertCircle, Sparkles } from 'lucide-react';
import { Link } from 'wouter';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface LimitUsageIndicatorProps {
  limitName: string;
  current: number;
  max: number;
  isUnlimited?: boolean;
  entityName: string; // e.g., "services", "providers", "bookings"
  className?: string;
}

export function LimitUsageIndicator({
  limitName,
  current,
  max,
  isUnlimited = false,
  entityName,
  className = ''
}: LimitUsageIndicatorProps) {
  const { t } = useTranslation('subscription');

  if (isUnlimited) {
    return null;
  }

  const percentage = (current / max) * 100;
  const isNearLimit = percentage >= 80;
  const isAtLimit = current >= max;

  return (
    <Card className={`p-4 border-l-4 ${isAtLimit ? 'border-l-danger bg-danger-soft/40' : isNearLimit ? 'border-l-warn bg-warn-soft/40' : 'border-l-line'} ${className}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {isAtLimit && <AlertCircle className="h-4 w-4 text-danger" />}
            <p className="text-label text-ink">
              {limitName}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-body-sm">
              <span className="text-soft">
                {current} / {max} {entityName}
              </span>
              <span className={`font-medium ${isAtLimit ? 'text-danger' : isNearLimit ? 'text-warn' : 'text-soft'}`}>
                {percentage.toFixed(0)}%
              </span>
            </div>

            <Progress
              value={percentage}
              indicatorClassName={isAtLimit ? 'bg-danger' : isNearLimit ? 'bg-warn' : undefined}
            />
          </div>

          {isAtLimit && (
            <p className="text-body-sm text-danger mt-2">
              {t('limitIndicator.limitReached', { entityName })}
            </p>
          )}
          {isNearLimit && !isAtLimit && (
            <p className="text-body-sm text-warn mt-2">
              {t('limitIndicator.approachingLimit')}
            </p>
          )}
        </div>

        {isNearLimit && (
          <div className="flex-shrink-0">
            <Button
              asChild
              size="lg"
              variant="primary"
              className="whitespace-nowrap"
            >
              <Link to="/admin/billing">
                <Sparkles className="h-4 w-4 mr-2" />
                {t('limitIndicator.upgrade')}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
